import * as Sentry from '@sentry/react';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import {
  AgentChatProvider,
  buildArtifactSurfaceContext,
  DeleteConversationModal,
  RenameConversationModal,
  useRenameConversationDialog,
  useAgentChatContext,
  useArtifactLineage,
  type ChatArtifactRef,
  type ChatAttachmentRef,
  type SurfaceContext,
} from '@/components/agent-chat';
import {
  ChatRailHeader,
  type ConversationMenuTarget,
} from '@/components/chat-workspace/chat-rail-header';
import { ChatWorkspace } from '@/components/chat-workspace/chat-workspace';
import { useCanvasMutationBridgeState } from '@/components/chat-workspace/use-surface-chat-host';
import { useWorkspaceSurface } from '@/components/chat-workspace/workspace-url';
import { ShareModal } from '@/components/share-modal';
import type {
  ArtifactGallerySurfaceDescriptor,
  SurfaceDescriptor,
} from '@/components/surface-host/surface-host';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/constants/routes';
import { useAgentConversations } from '@/hooks/queries/agents/use-agent-conversations';
import { usePrimaryAgent } from '@/hooks/queries/agents/use-primary-agent';
import { useGetArtifact } from '@/hooks/queries/artifacts/use-get-artifact';
import { useSurfaceConversation } from '@/hooks/queries/agents/use-surface-conversation';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { useWorkflowCanvasSurfaceContext } from '@/hooks/use-workflow-canvas-surface-context';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import {
  type AgentConversationSummary,
  getAgentConversationMessages,
} from '@/services/agents/config';
import { dispatchCanvasRun } from '@/stores/canvas-run-bridge-store';
import { dispatchCanvasApplyMutations } from '@/stores/canvas-apply-mutations-bridge-store';
import { useOrgStore } from '@/stores/org-store';
import type { Agent } from '@/types/agent-types';
import type { ArtifactListItem } from '@/types/artifacts';
import {
  downloadConversationMarkdown,
  type ExportableConversation,
} from '@/utils/download-conversation';

import { type ChatHomeLocationState } from './chat-home-location-state';
import {
  ChatHomeGreeting,
  ChatHomeStarterPrompts,
} from './chat-home-empty-state';

export function ChatHomePage() {
  const { data: primaryAgent, isLoading, error, refetch } = usePrimaryAgent();

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error || !primaryAgent) {
    return (
      <div className='flex flex-col items-center justify-center h-full gap-3 text-center p-6'>
        <p className='text-sm font-medium'>Chat is unavailable</p>
        <p className='text-sm text-muted-foreground max-w-md'>
          {error?.message ??
            'The organization primary agent could not be resolved.'}
        </p>
        <Button variant='outline' onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  return <ChatHomeConversation agent={primaryAgent} />;
}

function ChatHomeConversation({ agent }: { agent: Agent }) {
  const { conversationId: routeConversationId } = useParams<{
    conversationId?: string;
  }>();
  // Draft handed off from the Chats page prompt bar.
  const handoffRef = useRef<ChatHomeLocationState | null>(
    (useLocation().state as ChatHomeLocationState | null) ?? null,
  );
  const handoff = handoffRef.current;
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const orgId = currentOrg?.orgid;

  // Surface + chat-open state live in the URL so they survive cross-host
  // handoffs and the user can share or reload to the same view.
  // Chat defaults to open on chat-home (it is the point of the page).
  const {
    surface: urlSurface,
    chatOpen,
    setSurface,
    setChatOpen,
  } = useWorkspaceSurface({ chatOpenByDefault: true });

  // Artifacts this conversation has actually vouched for -- referenced in a
  // chat frame, or opened from a card inside the transcript -- as opposed to
  // one merely named by a `?artifact=` URL param, which carries no signal
  // about the viewer's access. Only the former is safe to treat as owned;
  // otherwise a shared chat-home link would open a stranger's artifact with
  // Rename/Delete/Share offered, same as the artifact route already avoids.
  const vouchedArtifactIdsRef = useRef<Set<string>>(new Set());

  // Enrich the surface with host-specific data that is not encoded in the URL.
  const surface: SurfaceDescriptor | null = useMemo(
    () =>
      urlSurface?.kind === 'artifact'
        ? {
            ...urlSurface,
            userRole: vouchedArtifactIdsRef.current.has(urlSurface.artifactId)
              ? 'artifact:owner'
              : undefined,
          }
        : urlSurface,
    [urlSurface],
  );

  const openArtifactId =
    surface?.kind === 'artifact' ? surface.artifactId : null;
  const openWorkflowId =
    surface?.kind === 'workflow' ? surface.workflowId : null;
  const openGalleryWorkflowId =
    surface?.kind === 'artifact_gallery' ? surface.workflowId : null;

  // A conversation id minted mid-stream on the first turn is adopted into
  // state, NOT via router navigation: pathname changes remount the whole
  // route subtree (RouterWithErrorBoundary keys on location.pathname),
  // which would destroy the live-streaming chat state mid-turn.
  const [adoptedConversationId, setAdoptedConversationId] = useState<
    string | null
  >(null);
  // A shared `/chat-home?workflow=<id>` link names no conversation of its
  // own, but the workflow it opens may already be linked to one -- resolve it
  // the same way the artifact route does, so the link resumes that thread
  // instead of silently starting a fresh one. Only kicks in when nothing has
  // already pinned a thread (a route id or a turn already adopted one);
  // artifact surfaces need no equivalent lookup here, since chat-home only
  // ever opens one from a frame or click inside the conversation already on
  // screen, so that conversation is already known.
  const surfaceForConversation =
    !routeConversationId &&
    !adoptedConversationId &&
    urlSurface?.kind === 'workflow'
      ? urlSurface
      : null;
  const {
    conversationId: startingConversationId,
    isResolving: isResolvingSurfaceConversation,
  } = useSurfaceConversation(surfaceForConversation, agent.id);
  const conversationId =
    routeConversationId ??
    adoptedConversationId ??
    (isResolvingSurfaceConversation ? null : startingConversationId);
  const conversationLinkResolving =
    !routeConversationId &&
    !adoptedConversationId &&
    isResolvingSurfaceConversation;

  const { data: conversations = [], refetch: refetchConversations } =
    useAgentConversations(agent.id);

  // What this conversation's frames said about each artifact: what names one
  // in a context marker, and what tells one version of a document from the
  // next. Reset when the conversation changes, which the hook handles.
  const {
    absorb: absorbArtifacts,
    keyFor: artifactContextKey,
    meta: artifactMeta,
  } = useArtifactLineage(conversationId);

  const showArtifact = useCallback(
    (artifactId: string) => {
      // Opened from a card inside this conversation's own transcript, so the
      // conversation vouches for it.
      vouchedArtifactIdsRef.current.add(artifactId);
      setSurface({ kind: 'artifact', artifactId });
    },
    [setSurface],
  );

  const openWorkflow = useCallback(
    (workflowId: string, runId?: string) => {
      setSurface({ kind: 'workflow', workflowId, runId });
    },
    [setSurface],
  );

  // Opened from a card inside the artifact gallery, carrying user_role since
  // the detail endpoint does not return it.
  const openArtifactFromGallery = useCallback(
    (artifact: ArtifactListItem) => {
      vouchedArtifactIdsRef.current.add(artifact.id);
      setSurface({
        kind: 'artifact',
        artifactId: artifact.id,
        userRole: artifact.user_role,
      });
    },
    [setSurface],
  );

  const handleCloseSurface = useCallback(() => setSurface(null), [setSurface]);

  const handleViewWorkflowArtifacts = useCallback(
    (workflowId: string) => {
      setSurface({ kind: 'artifact_gallery', workflowId, locked: true });
    },
    [setSurface],
  );

  const handleArtifactFrame = useCallback(
    (artifact: ChatArtifactRef) => {
      const artifactId = artifact.artifact_id;
      if (!artifactId) return;
      // The agent reported it during this conversation's own turn, so the
      // conversation vouches for it.
      vouchedArtifactIdsRef.current.add(artifactId);
      absorbArtifacts([artifact]);
      // Keep the freshest orchestrator output in the surface pane. No-op
      // while the pane is closed: frames alone don't force it open.
      if (urlSurface?.kind === 'artifact') {
        setSurface({ kind: 'artifact', artifactId });
      }
    },
    [urlSurface, setSurface, absorbArtifacts],
  );

  const { data: openArtifact, isPending: openArtifactPending } =
    useGetArtifact(openArtifactId);
  // An artifact nobody has named yet: the frames may still name it (a
  // conversation that just loaded), or the fetch will. Either way a marker
  // written now would read "an artifact" for good.
  const surfaceResolving =
    !openWorkflowId &&
    !!openArtifactId &&
    !artifactMeta.get(openArtifactId)?.title &&
    openArtifactPending;

  const {
    isMountedCanvasLocked,
    applyMutationsDisabled,
    applyMutationsDisabledReason,
  } = useCanvasMutationBridgeState();

  // The pane beside this rail mounts the real editor, so the agent gets the
  // same live graph it would get from the editor's own chat — without it the
  // agent cannot see the nodes, let alone run them.
  const canvasSurfaceContext = useWorkflowCanvasSurfaceContext(
    openWorkflowId,
    isMountedCanvasLocked,
  );

  const surfaceContext = useMemo<SurfaceContext | null>(() => {
    if (openWorkflowId) {
      return canvasSurfaceContext;
    }
    // A gallery is a surface of its own. Reporting nothing here would ground
    // the agent in an empty screen and record the user as having gone back to
    // general chat, while they are looking at that workflow's files.
    if (openGalleryWorkflowId) {
      return {
        surface: 'artifact_gallery',
        workflow_id: openGalleryWorkflowId,
      };
    }
    if (!openArtifactId) return null;
    return buildArtifactSurfaceContext({
      artifactId: openArtifactId,
      artifactContextKey,
      meta: artifactMeta.get(openArtifactId),
      artifact: openArtifact,
    });
  }, [
    openWorkflowId,
    canvasSurfaceContext,
    openGalleryWorkflowId,
    openArtifactId,
    openArtifact,
    artifactMeta,
    artifactContextKey,
  ]);

  const handleConversationChange = useCallback(
    (id: string) => {
      setAdoptedConversationId(id);
      // Cosmetic URL update so reloads and shared links resolve the
      // conversation; bypasses the router to avoid the remount above.
      // Reads the real, current search rather than react-router's own
      // searchParams: useWorkspaceSurface now writes surface params via
      // window.history.replaceState directly (see workspace-url.ts), which
      // react-router's copy does not observe, so it can be stale here.
      const search = window.location.search;
      const pathname = `${orgId ? `/o/${orgId}` : ''}${ROUTES.CHAT_HOME.CONVERSATION.replace(':conversationId', id)}`;
      window.history.replaceState(
        window.history.state,
        '',
        `${pathname}${search}`,
      );
    },
    [orgId],
  );

  const handleResetConversation = useCallback(() => {
    setAdoptedConversationId(null);
  }, []);

  const composerPlaceholder =
    !conversationId && openArtifactId && openArtifact?.title
      ? `Ask for changes to '${openArtifact.title}'`
      : undefined;

  return (
    <AgentChatProvider
      agentId={agent.id}
      conversationId={conversationId}
      conversationLinkResolving={conversationLinkResolving}
      onConversationChange={handleConversationChange}
      onTurnComplete={refetchConversations}
      surfaceContext={surfaceContext}
      openWorkflowId={openWorkflowId}
      surfaceResolving={surfaceResolving}
      onArtifact={handleArtifactFrame}
      onArtifactHistory={absorbArtifacts}
      onOpenArtifact={showArtifact}
      onOpenWorkflow={openWorkflow}
      onRunWorkflow={dispatchCanvasRun}
      onApplyMutations={dispatchCanvasApplyMutations}
      applyMutationsDisabled={applyMutationsDisabled}
      applyMutationsDisabledReason={applyMutationsDisabledReason}
    >
      <ChatHomeContent
        conversations={conversations}
        onResetConversation={handleResetConversation}
        initialDraft={handoff?.chatDraft}
        initialAttachments={handoff?.attachments}
        autoSendInitialDraft={handoff?.autoSend ?? false}
        surface={surface}
        onCloseSurface={handleCloseSurface}
        composerPlaceholder={composerPlaceholder}
        chatOpen={chatOpen}
        onChatOpenChange={setChatOpen}
        onViewWorkflowArtifacts={handleViewWorkflowArtifacts}
        onOpenGalleryArtifact={openArtifactFromGallery}
        onGalleryChange={setSurface}
      />
    </AgentChatProvider>
  );
}

interface ChatHomeContentProps {
  conversations: AgentConversationSummary[];
  onResetConversation: () => void;
  initialDraft?: string;
  initialAttachments?: ChatAttachmentRef[];
  autoSendInitialDraft?: boolean;
  /**
   * The surface to show beside the rail, already decoded from the URL and
   * enriched by the host. Passed whole rather than as a set of ids so a new
   * surface kind cannot be silently dropped on the way down.
   */
  surface: SurfaceDescriptor | null;
  onCloseSurface: () => void;
  composerPlaceholder?: string;
  chatOpen: boolean;
  onChatOpenChange: (open: boolean) => void;
  onViewWorkflowArtifacts?: (workflowId: string) => void;
  onOpenGalleryArtifact?: (artifact: ArtifactListItem) => void;
  onGalleryChange?: (surface: ArtifactGallerySurfaceDescriptor) => void;
}

function ChatHomeContent({
  conversations,
  onResetConversation,
  initialDraft,
  initialAttachments,
  autoSendInitialDraft = false,
  surface,
  onCloseSurface,
  composerPlaceholder,
  chatOpen,
  onChatOpenChange,
  onViewWorkflowArtifacts,
  onOpenGalleryArtifact,
  onGalleryChange,
}: ChatHomeContentProps) {
  const navigate = useOrgNavigate();
  const routerNavigate = useNavigate();
  const location = useLocation();
  const {
    orgId,
    agent,
    agentId,
    conversationId,
    writeAccess,
    conversationTitle,
    clearMessages,
    focusComposer,
    sendMessage,
    setComposerDraft,
  } = useAgentChatContext();
  const { isChatSharingEnabled } = useLaunchDarkly();
  const [shareOpen, setShareOpen] = useState(false);
  const { renameTarget, openRename, renameProps } =
    useRenameConversationDialog<ConversationMenuTarget>();
  const [deleteTarget, setDeleteTarget] =
    useState<ConversationMenuTarget | null>(null);
  const isOwner = writeAccess === 'allowed';
  // Whether this host offers sharing at all. Whether there is yet a thread to
  // share is the header's business, so its entry greys out beside the other
  // conversation-scoped ones instead of vanishing.
  const sharingAvailable = isChatSharingEnabled && isOwner;

  // The chat hook clears its message list in a mount effect whenever there is
  // no conversation id. Provider effects run after this component's, so a
  // handed-off draft sent on mount would be wiped -- wait a commit.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const hasHandoff = !!initialDraft || !!initialAttachments?.length;
  // Until the deferred send lands, the panel must not read as an empty chat:
  // the onboarding greeting would paint for a frame and the composer would
  // jump from centre to bottom.
  const [pendingFirstTurn, setPendingFirstTurn] = useState(
    hasHandoff && autoSendInitialDraft,
  );

  const handoffConsumedRef = useRef(false);
  useEffect(() => {
    if (!mounted || handoffConsumedRef.current || !hasHandoff) return;
    handoffConsumedRef.current = true;
    // Drop the router state before sending so a reload can't replay the turn.
    // The pathname is unchanged, so RouterWithErrorBoundary (keyed on
    // pathname) does not remount and the stream survives.
    routerNavigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
    if (autoSendInitialDraft) {
      sendMessage(initialDraft ?? '', initialAttachments);
      setPendingFirstTurn(false);
    } else if (initialDraft) {
      setComposerDraft(initialDraft);
    }
  }, [
    mounted,
    hasHandoff,
    initialDraft,
    initialAttachments,
    autoSendInitialDraft,
    location.pathname,
    location.search,
    routerNavigate,
    sendMessage,
    setComposerDraft,
  ]);

  const startFreshConversation = useCallback(() => {
    onResetConversation();
    clearMessages();
    focusComposer();
  }, [onResetConversation, clearMessages, focusComposer]);

  const handleNewConversation = () => {
    startFreshConversation();
    navigate(ROUTES.CHAT_HOME.ROOT);
  };

  // A thread started on this page only reaches the address bar through
  // replaceState, so the router still sits on the bare chat-home path and a
  // link back to it neither changes the pathname nor remounts the page.
  const lastLocationKeyRef = useRef(location.key);
  useEffect(() => {
    if (location.key === lastLocationKeyRef.current) return;
    lastLocationKeyRef.current = location.key;
    if (
      (location.state as ChatHomeLocationState | null)?.startedNewConversation
    ) {
      startFreshConversation();
    }
  }, [location.key, location.state, startFreshConversation]);

  const handleSelectConversation = (id: string) => {
    if (id === conversationId) return;
    navigate(ROUTES.CHAT_HOME.CONVERSATION.replace(':conversationId', id));
  };

  const handleDownloadConversation = async () => {
    if (!orgId || !conversationId) return;
    const activeConversation = conversations.find(
      (c) => c.conversation_id === conversationId,
    );
    const exportable: ExportableConversation = {
      title: activeConversation?.title ?? conversationTitle ?? undefined,
      preview: activeConversation?.preview,
      created_at: activeConversation?.created_at,
      last_message_at: activeConversation?.last_message_at,
    };
    try {
      const msgs = await getAgentConversationMessages(
        orgId,
        agentId,
        conversationId,
      );
      downloadConversationMarkdown(exportable, msgs, {
        agentName: agent?.name,
      });
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          context: 'chat-home-download-conversation',
          orgId,
          agentId,
          conversationId,
        },
      });
    }
  };

  return (
    <>
      <ChatWorkspace
        agentId={agentId}
        surface={surface}
        // Here a workflow surface's X means "go full-page chat", which is
        // meaningless with the rail already hidden — there is nothing behind it
        // but the chat. The artifact surface keeps its X, since that one means
        // "back to the chat's own view" and still lands somewhere.
        onCloseSurface={
          surface?.kind === 'workflow' && !chatOpen ? undefined : onCloseSurface
        }
        // Delete has to retire the pane even when the X is withheld, and the
        // chat behind it is somewhere to go -- unlike the composer list, it
        // keeps the conversation the workflow was opened from.
        onWorkflowDeleted={onCloseSurface}
        composerPlaceholder={composerPlaceholder}
        skillsEnabled
        workflowCommands
        emptyState={<ChatHomeGreeting />}
        emptyStateActions={<ChatHomeStarterPrompts />}
        pendingFirstTurn={pendingFirstTurn}
        collapsed={!chatOpen}
        onCollapsedChange={(c) => onChatOpenChange(!c)}
        onViewWorkflowArtifacts={onViewWorkflowArtifacts}
        onOpenGalleryArtifact={onOpenGalleryArtifact}
        onGalleryChange={onGalleryChange}
        renderRailHeader={({ surfaceOpen, onCollapse }) => (
          <ChatRailHeader
            conversations={conversations}
            conversationId={conversationId}
            conversationTitle={conversationTitle}
            onNewConversation={handleNewConversation}
            onSelectConversation={handleSelectConversation}
            onShareConversation={
              sharingAvailable ? () => setShareOpen(true) : undefined
            }
            onDownloadConversation={handleDownloadConversation}
            onRenameConversation={isOwner ? openRename : undefined}
            onDeleteConversation={isOwner ? setDeleteTarget : undefined}
            onCollapse={surfaceOpen ? onCollapse : undefined}
          />
        )}
      />
      {renameTarget && (
        <RenameConversationModal
          agentId={agentId}
          conversationId={renameTarget.id}
          currentTitle={renameTarget.title}
          {...renameProps}
        />
      )}
      {deleteTarget && (
        <DeleteConversationModal
          agentId={agentId}
          conversationId={deleteTarget.id}
          label={deleteTarget.label}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            if (conversationId === deleteTarget.id) handleNewConversation();
          }}
        />
      )}
      {sharingAvailable && conversationId && (
        <ShareModal
          resourceType='chat'
          resourceId={conversationId}
          orgId={orgId ?? null}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}
    </>
  );
}
